import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";

interface ViewportLazyProps {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
}

/**
 * SSR renders `fallback`. On the client the real tree loads only when the
 * placeholder approaches the viewport, so below-the-fold homepage chunks
 * stay out of the initial network waterfall.
 */
const ViewportLazy = ({
  children,
  fallback = null,
  rootMargin = "600px",
}: ViewportLazyProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient || inView) return;
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isClient, inView, rootMargin]);

  return (
    <div ref={ref}>
      {!isClient || !inView ? fallback : <Suspense fallback={fallback}>{children}</Suspense>}
    </div>
  );
};

export default ViewportLazy;
